// wallpaperLibrary/WallpaperColorStrip.jsx
//
// The "Color theme" strip for the wallpaper library (deck 01-wallpaper-svg-library,
// notes7 rework — Gordon: "do not show the same SVG pattern multiple times with
// different colors"). A selected design is shown ONCE in the grid; THIS strip is how
// its colour is chosen — a readout + the curated palette swatches — instead of the
// rejected 40-tile recolor grid. Mirrors the loops library's LoopColorStrip pattern
// (a future refactor should extract a single shared ColorThemeStrip; see the unit
// deliverable — the two palette shapes differ today, so they are kept separate to
// avoid destabilising the passing loops tests).
//
// G-56: every swatch DRIVES a real value change via onChange — selecting a swatch
// re-applies the chosen design in the new palette.

import React from "react";
import PropTypes from "prop-types";

// Brand accent for the selected swatch chrome (matches loops / stock / grid).
const SELECT_ACCENT = "#9B5AF8";

const Swatch = ({ bg, fg, active, label, onClick }) => (
  <button
    type="button"
    aria-label={label}
    aria-pressed={active}
    title={label}
    onClick={onClick}
    style={{
      width: 26,
      height: 26,
      borderRadius: "50%",
      background: bg,
      border: active ? `2px solid ${SELECT_ACCENT}` : "1px solid #e1e1e1",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      padding: 0,
    }}
  >
    <span style={{ width: 12, height: 12, borderRadius: "50%", background: fg }} />
  </button>
);

const WallpaperColorStrip = ({ palettes, value, onChange }) => {
  const currentName =
    palettes.find((p) => p.id === value)?.name || palettes[0]?.name || "";

  return (
    <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid #eee" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8,
        }}
      >
        <span
          style={{
            fontFamily: "DM Sans, sans-serif",
            fontSize: 13,
            fontWeight: 700,
            color: "#123051",
          }}
        >
          Color theme
        </span>
        <span
          style={{
            fontFamily: "DM Sans, sans-serif",
            fontSize: 12,
            color: "#565556",
          }}
        >
          {currentName}
        </span>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {palettes.map((p) => (
          <Swatch
            key={p.id}
            // Two-tone preview: the palette's sky tint behind its darkest layer.
            bg={p.sky[1]}
            fg={p.layers[2]}
            label={p.name}
            active={value === p.id}
            onClick={() => onChange(p.id)}
          />
        ))}
      </div>

      <div
        style={{
          fontFamily: "DM Sans, sans-serif",
          fontSize: 11,
          color: "#565556",
          marginTop: 8,
        }}
      >
        Recolors the selected design — the artwork is untouched.
      </div>
    </div>
  );
};

WallpaperColorStrip.propTypes = {
  palettes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      sky: PropTypes.arrayOf(PropTypes.string).isRequired,
      layers: PropTypes.arrayOf(PropTypes.string).isRequired,
    })
  ).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

export default WallpaperColorStrip;
